import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSEO } from "@/hooks/useSEO";
import { CTALink } from "@/components/common/CTALink";
import { Reveal } from "@/components/common/Reveal";
import { SectionHeading } from "@/components/common/SectionHeading";
import { FAQ } from "@/components/blocks/FAQ";
import { CtaBand } from "@/components/blocks/CtaBand";
import { WhatsAppProductHero } from "@/components/visuals/WhatsAppProductHero";
import { TeamInboxMock } from "@/components/visuals/TeamInboxMock";
import { FlowBuilderMock } from "@/components/visuals/FlowBuilderMock";
import { CampaignDashboard } from "@/components/visuals/CampaignDashboard";
import { WhatsAppChatMock } from "@/components/visuals/WhatsAppChatMock";
import { ApiTabs } from "@/components/visuals/ApiTabs";
import { contactLink } from "@/data/site";
import { WA_TRUST, WA_USE_CASES } from "@/data/whatsapp";

const PLATFORM_TOOLS = [
  "WhatsApp Business API", "Bulk Messaging", "Campaign Manager", "Shared Team Inbox",
  "Multi-Agent", "Rule-Based Chatbots", "Flow-Based Chatbots", "Templates",
  "Contact Management", "Segmentation", "Analytics", "API & Webhooks",
  "WhatsApp Calling", "AI Calling",
];

const TALK = contactLink("WhatsApp Automation");
const PRICING = "/whatsapp-automation/pricing";

const CONVO_AUTOMATION = [
  "Welcome messages", "Automated responses", "Ice-breaker questions", "Quick actions",
  "Rule-based automation", "Customer journey automation",
];

const CHATBOT_USES = ["Lead qualification", "FAQs", "Order updates", "Appointment flows", "Customer support", "Data collection", "Sales enquiries", "Routing to human agents"];

export default function WhatsAppAutomation() {
  useSEO({
    title: "WhatsApp Automation & Business API Platform | E Telecom",
    description:
      "Automate WhatsApp campaigns, customer conversations, chatbots and team collaboration with E Telecom's WhatsApp Business automation platform.",
    path: "/whatsapp-automation",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "E Telecom WhatsApp Automation",
      description: "WhatsApp Business automation platform with campaigns, shared team inbox, chatbots and API integration.",
      brand: { "@type": "Brand", name: "E Telecom" },
    },
  });

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden et-hero-wash">
        <div className="absolute inset-0 et-dot-grid opacity-[0.4] pointer-events-none" />
        <div className="et-container relative py-16 sm:py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
            <div>
              <Reveal>
                <span className="et-eyebrow"><Icons.MessageSquareText className="h-3.5 w-3.5" /> E Telecom WhatsApp Automation</span>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="font-display text-3xl sm:text-4xl lg:text-[3.25rem] font-bold tracking-tight text-slate-900 mt-5 leading-[1.08]">
                  Manage Every WhatsApp Customer Conversation From One Powerful Platform
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
                  Run campaigns, automate conversations, collaborate through a shared team inbox and connect WhatsApp with your business systems using the official WhatsApp Business Platform.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <CTALink to={PRICING} variant="primary" data-testid="wa-hero-view-plans" withArrow>View Plans</CTALink>
                  <CTALink to="/whatsapp-automation#features" variant="secondary" data-testid="wa-hero-explore-features">Explore Features</CTALink>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
                  {WA_TRUST.map((t) => {
                    const Icon = Icons[t.icon] || Icons.Check;
                    return (
                      <span key={t.label} className="inline-flex items-center gap-2 text-xs sm:text-[13px] font-medium text-slate-600">
                        <Icon className="h-4 w-4 text-[var(--et-primary)]" /> {t.label}
                      </span>
                    );
                  })}
                </div>
              </Reveal>
            </div>
            <div className="lg:pl-4"><Reveal delay={0.15}><WhatsAppProductHero /></Reveal></div>
          </div>
        </div>
      </section>

      {/* PLATFORM ECOSYSTEM STRIP */}
      <section className="border-y border-slate-200 bg-white py-10">
        <div className="et-container">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-slate-400">A complete WhatsApp platform — not just bulk messaging</p>
          <div className="mt-6 flex flex-wrap justify-center gap-2.5">
            {PLATFORM_TOOLS.map((t, i) => (
              <Reveal key={t} delay={(i % 8) * 0.02}>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-[13px] font-medium text-slate-700">
                  <Icons.Check className="h-3.5 w-3.5 text-[#25D366]" /> {t}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SHARED TEAM INBOX */}
      <section className="et-section">
        <div className="et-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <span className="et-eyebrow">One Number. Your Entire Team.</span>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mt-4 leading-tight">
                Collaborate With Customers From a Shared WhatsApp Inbox
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Allow sales, support and customer-success teams to manage WhatsApp conversations together — without relying on a single company phone.
              </p>
              <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {["Add team members", "Assign conversations", "Track conversation ownership", "Send quick replies", "Use approved WhatsApp templates", "View conversation history", "Handle multiple chats at once", "Search and organise easily"].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700"><Icons.Check className="h-4 w-4 text-[var(--et-primary)] mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <CTALink to={PRICING} variant="primary" withArrow>View Plans</CTALink>
                <CTALink to={TALK} variant="secondary">Talk to Sales</CTALink>
              </div>
            </Reveal>
            <Reveal delay={0.1}><TeamInboxMock /></Reveal>
          </div>
        </div>
      </section>

      {/* CORE PLATFORM CAPABILITIES (alternating) */}
      <section id="features" className="et-section bg-white scroll-mt-20">
        <div className="et-container">
          <SectionHeading eyebrow="Platform Capabilities" title="Everything You Need to Scale WhatsApp" subtitle="A complete toolkit to reach, automate and manage WhatsApp communication across your teams." />

          <div className="mt-14 space-y-16 lg:space-y-24">
            <FeatureRow
              icon="Megaphone"
              title="Bulk Messaging"
              desc="Send personalized WhatsApp communication to opted-in audiences using approved templates."
              points={["Rich-media templates", "Audience selection", "Contact segmentation", "Personalization variables", "Delivery & read tracking"]}
              visual={<CampaignDashboard />}
            />
            <FeatureRow
              reverse
              icon="Workflow"
              title="Visual Flow Builder"
              desc="Build multi-step automated WhatsApp journeys visually — no code required."
              points={["Drag-and-drop workflow builder", "Conditional logic & routing", "List messages, buttons & media", "Template messages", "Lead qualification workflows"]}
              visual={<FlowBuilderMock />}
            />
            <FeatureRow
              icon="LayoutDashboard"
              title="Campaign Manager"
              desc="Create, schedule and manage WhatsApp campaigns with full visibility."
              points={["Campaign creation & scheduling", "Recipient selection", "Template preview & dynamic variables", "Launch / pause controls", "Recipient tracking & delivery analytics"]}
              visual={<CampaignDashboard />}
            />
            <FeatureRow
              reverse
              icon="Code2"
              title="API Integration"
              desc="Connect supported CRM, ERP, websites and internal systems where available. Built to be integration-ready."
              points={["Send messages programmatically", "Templates & dynamic variables", "Webhooks on supported plans", "Clear, developer-friendly responses"]}
              visual={<ApiTabs />}
            />
          </div>

          {/* Conversational automation compact row */}
          <Reveal className="mt-16">
            <div className="et-card p-6 sm:p-8">
              <div className="flex items-center gap-3">
                <span className="et-icon-box"><Icons.Bot className="h-5 w-5" /></span>
                <h3 className="font-display text-xl font-semibold text-slate-900">Conversational Automation</h3>
              </div>
              <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {CONVO_AUTOMATION.map((c) => (
                  <span key={c} className="inline-flex items-center gap-2 rounded-xl border border-slate-200/70 bg-slate-50/60 px-4 py-2.5 text-sm font-medium text-slate-700">
                    <Icons.Check className="h-4 w-4 text-[var(--et-primary)]" /> {c}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SMART CHATBOT SECTION */}
      <section className="et-section">
        <div className="et-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal className="order-2 lg:order-1"><WhatsAppChatMock /></Reveal>
            <Reveal delay={0.1} className="order-1 lg:order-2">
              <span className="et-eyebrow">Smart Chatbots</span>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mt-4 leading-tight">
                Automate Conversations Without Losing the Human Touch
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Use rule-based and flow-based chatbots to handle routine conversations instantly — and hand off seamlessly to a human agent whenever needed.
              </p>
              <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {CHATBOT_USES.map((c) => (
                  <span key={c} className="flex items-start gap-2 text-sm text-slate-700"><Icons.Check className="h-4 w-4 text-[#25D366] mt-0.5 shrink-0" /> {c}</span>
                ))}
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <CTALink to={PRICING} variant="primary" withArrow>View Plans</CTALink>
                <CTALink to={TALK} variant="secondary">Talk to Sales</CTALink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* USE CASES TABS */}
      <section className="et-section bg-white">
        <div className="et-container">
          <SectionHeading eyebrow="Use Cases" title="Built for Real Business Workflows" subtitle="See how teams put WhatsApp automation to work across the customer journey." />
          <Reveal className="mt-10 max-w-4xl mx-auto">
            <Tabs defaultValue="Marketing" className="w-full">
              <TabsList className="flex flex-wrap h-auto w-full justify-center gap-1 bg-slate-100 p-1">
                {Object.keys(WA_USE_CASES).map((k) => (
                  <TabsTrigger key={k} value={k} data-testid={`wa-usecase-${k.toLowerCase()}`} className="text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--et-primary)]">{k}</TabsTrigger>
                ))}
              </TabsList>
              {Object.entries(WA_USE_CASES).map(([k, v]) => {
                const Icon = Icons[v.icon] || Icons.Sparkles;
                return (
                  <TabsContent key={k} value={k} className="mt-6">
                    <div className="et-card p-6 sm:p-8">
                      <div className="flex items-center gap-3">
                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[rgba(37,99,235,0.1)] text-[var(--et-primary)]"><Icon className="h-6 w-6" /></span>
                        <h3 className="font-display text-xl font-semibold text-slate-900">{k}</h3>
                      </div>
                      <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                        {v.points.map((p) => (
                          <li key={p} className="flex items-start gap-2 text-sm text-slate-700"><Icons.CheckCircle2 className="h-5 w-5 text-[var(--et-primary)] mt-0.5 shrink-0" /> {p}</li>
                        ))}
                      </ul>
                    </div>
                  </TabsContent>
                );
              })}
            </Tabs>
          </Reveal>
        </div>
      </section>

      <FAQ
        title="WhatsApp Automation FAQs"
        items={[
          { q: "Is WhatsApp Automation a complete platform?", a: "Yes. It includes campaigns, a shared team inbox, rule-based and flow-based chatbots, templates, contact management, analytics and API integration — all in one place." },
          { q: "Does it use the official WhatsApp Business Platform?", a: "Yes. E Telecom WhatsApp Automation is built on the official WhatsApp Business Platform for reliable, compliant business messaging." },
          { q: "Can multiple team members use one number?", a: "Yes. The shared team inbox lets your sales, support and success teams collaborate on the same WhatsApp number with conversation assignment and ownership." },
          { q: "Can I integrate it with my CRM or website?", a: "API access is available on Business and Advanced plans, enabling integration with supported CRM, ERP, websites and internal systems." },
          { q: "How do I choose a plan?", a: "Compare Starter, Business and Advanced on our WhatsApp pricing page, or talk to our team for a recommendation based on your needs." },
        ]}
      />

      <CtaBand
        title="Bring your whole team onto WhatsApp"
        subtitle="Explore plans built for WhatsApp campaigns, automation and team collaboration — or talk to our team for guidance."
        primary={{ to: PRICING, label: "View Plans" }}
        secondary={{ to: TALK, label: "Talk to Sales" }}
      />
    </>
  );
}

/* Alternating feature row: text + large visual */
const FeatureRow = ({ icon, title, desc, points, visual, reverse }) => {
  const Icon = Icons[icon] || Icons.Sparkles;
  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <Reveal className={reverse ? "lg:order-2" : ""}>
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[rgba(37,99,235,0.1)] text-[var(--et-primary)]"><Icon className="h-6 w-6" /></span>
        <h3 className="font-display text-2xl font-semibold text-slate-900 mt-5">{title}</h3>
        <p className="mt-3 text-slate-600 leading-relaxed">{desc}</p>
        <ul className="mt-5 space-y-2.5">
          {points.map((p) => (
            <li key={p} className="flex items-start gap-2 text-sm text-slate-700"><Icons.Check className="h-4 w-4 text-[var(--et-primary)] mt-0.5 shrink-0" /> {p}</li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.1} className={reverse ? "lg:order-1" : ""}>{visual}</Reveal>
    </div>
  );
};
